import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { useDatabase } from '../context/DatabaseContext';
import { createSkill } from '../services/skillApi';
import { ArrowLeft, Loader, Save, Eye, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { splitSkillMarkdown, stringifySkillMarkdown } from '../utils/skillParser';

const DEFAULT_SKILL_BODY = `
## For Recommended Priorities

**Use this skill when** DATA or topic analysis shows … (one narrow condition tied to your domain).

**Enrich \`plans\` with:** short ordered \`steps\`, a realistic **owner**, and **expected_impact** phrased as testable outcomes—**all numbers and \`baseline_query_ids\` only from DATA** in the prompt.

**Skip when** … (when another area owns the signal).

**Angles:** 2–4 bullet hints the model may use to structure work—no invented metrics or targets.
`.trim();

/**
 * SkillCreate — Markdown-first skill creation inside a subfolder (URL ``…/folder/:folderSlug/create``).
 */
export const SkillCreate = () => {
    const { t } = useTranslation(['pages-misc-2', 'common']);
    const navigate = useNavigate();
    const location = useLocation();
    const { databaseId, folderSlug: folderSlugParam } = useParams();
    const { databases } = useDatabase();
    const { showToast } = useToast();

    const folderSlug = folderSlugParam || '';

    const databaseResolved = useMemo(() => {
        const fromState = location.state?.database;
        if (fromState?.id) return fromState;
        const id = parseInt(databaseId, 10);
        if (!Number.isFinite(id)) return null;
        return databases.find((d) => d.id === id) || { id, name: `Database ${id}` };
    }, [location.state, databaseId, databases]);

    const [saving, setSaving] = useState(false);

    const [name, setName] = useState('New Skill');
    const [skillSlug, setSkillSlug] = useState('new-skill');
    const [description, setDescription] = useState('');
    const [model, setModel] = useState('sonnet');
    const [skillMarkdown, setSkillMarkdown] = useState(() =>
        stringifySkillMarkdown(
            { name: 'New Skill', description: '', model: 'sonnet' },
            DEFAULT_SKILL_BODY
        )
    );
    const [activeTab, setActiveTab] = useState('edit');

    const textareaRef = useRef(null);

    useEffect(() => {
        if (!folderSlug?.trim()) {
            if (databaseId) navigate(`/skills/${databaseId}`, { replace: true });
            else navigate('/skills', { replace: true });
        }
    }, [folderSlug, databaseId, navigate]);

    const onMarkdownChange = (e) => {
        const v = e.target.value;
        setSkillMarkdown(v);
        const { frontmatter } = splitSkillMarkdown(v);
        if (frontmatter.name !== undefined) setName(frontmatter.name);
        if (frontmatter.description !== undefined) setDescription(frontmatter.description);
        if (frontmatter.model !== undefined) setModel(frontmatter.model);
    };

    const onNameChange = (e) => {
        const v = e.target.value;
        setName(v);
        const { body } = splitSkillMarkdown(skillMarkdown);
        setSkillMarkdown(stringifySkillMarkdown({ name: v, description, model }, body));
    };

    const onDescriptionChange = (e) => {
        const v = e.target.value;
        setDescription(v);
        const { body } = splitSkillMarkdown(skillMarkdown);
        setSkillMarkdown(stringifySkillMarkdown({ name, description: v, model }, body));
    };

    const onModelChange = (e) => {
        const v = e.target.value;
        setModel(v);
        const { body } = splitSkillMarkdown(skillMarkdown);
        setSkillMarkdown(stringifySkillMarkdown({ name, description, model: v }, body));
    };

    const { body: previewBody } = splitSkillMarkdown(skillMarkdown);

    const folderListUrl = `/skills/${databaseId}/folder/${encodeURIComponent(folderSlug)}`;

    const handleBack = () => navigate(folderListUrl);

    const handleSave = async () => {
        if (!databaseResolved?.id) {
            showToast(t('pages-misc-2:skillCreate.missingDatabaseContext'), 'error');
            return;
        }
        if (!folderSlug.trim()) {
            showToast(t('pages-misc-2:skillCreate.missingSubfolder'), 'error');
            return;
        }
        if (!name.trim()) {
            showToast(t('pages-misc-2:skillCreate.skillNameRequired'), 'error');
            return;
        }
        if (!skillSlug.trim()) {
            showToast(t('pages-misc-2:skillCreate.skillSlugRequired'), 'error');
            return;
        }

        setSaving(true);
        try {
            const { body } = splitSkillMarkdown(skillMarkdown);
            const payload = {
                database_id: databaseResolved.id,
                name: name.trim(),
                skill_slug: skillSlug.trim(),
                description: description.trim(),
                model: model || null,
                body: body.trimEnd() + '\n',
                subfolder: folderSlug.trim()
            };
            const resp = await createSkill(payload);
            if (resp.data?.id) {
                showToast(t('pages-misc-2:skillCreate.skillCreatedSuccess'), 'success');
                navigate(folderListUrl);
                return;
            }
            showToast(t('pages-misc-2:skillCreate.failedToCreateSkill'), 'error');
        } catch (e) {
            console.error('Failed to create skill:', e);
            showToast(t('pages-misc-2:skillCreate.failedToCreateSkill'), 'error');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [skillMarkdown, activeTab]);

    if (!folderSlug.trim()) {
        return null;
    }

    return (
        <div className="page-content pb-20">
            <div className="mb-6">
                <button
                    onClick={handleBack}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('common:back')}
                </button>

                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('pages-misc-2:skillCreate.createSkill')}</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            {t('pages-misc-2:skillCreate.subfolder')}:{' '}
                            <code className="bg-gray-100 px-1.5 py-0.5 rounded text-primary-800">
                                skills/{folderSlug}/
                            </code>
                        </p>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`px-6 py-2.5 rounded-xl font-bold text-[11px] uppercase tracking-widest flex items-center space-x-2 transition-all shadow-lg ${
                            saving
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                                : 'bg-primary-600 text-white hover:bg-primary-700 shadow-primary-100 hover:-translate-y-0.5 active:translate-y-0'
                        }`}
                    >
                        {saving ? (
                            <>
                                <Loader className="w-4 h-4 animate-spin" />
                                <span>{t('common:creating')}</span>
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                <span>{t('pages-misc-2:skillCreate.saveSkill')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('pages-misc-2:skillCreate.skillDetails')}</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('pages-misc-2:skillCreate.skillNameLabel')}</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={onNameChange}
                                    placeholder={t('pages-misc-2:skillCreate.skillNamePlaceholder')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('pages-misc-2:skillCreate.skillSlugLabel')}
                                </label>
                                <input
                                    type="text"
                                    value={skillSlug}
                                    onChange={(e) => setSkillSlug(e.target.value)}
                                    placeholder={t('pages-misc-2:skillCreate.skillSlugPlaceholder')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {t('pages-misc-2:skillCreate.skillSlugHelpPrefix')} <code className="bg-gray-100 px-1 rounded">/</code>{' '}
                                    {t('pages-misc-2:skillCreate.skillSlugHelpMiddle')}{' '}
                                    <strong>{t('pages-misc-2:skillCreate.skillSlugHelpLast')}</strong> {t('pages-misc-2:skillCreate.skillSlugHelpSegment')}{' '}
                                    <code className="bg-gray-100 px-1 rounded">team/onboarding</code> →{' '}
                                    <code className="bg-gray-100 px-1 rounded">
                                        skills/{folderSlug}/onboarding.md
                                    </code>
                                    .
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:description')}</label>
                                <textarea
                                    value={description}
                                    onChange={onDescriptionChange}
                                    placeholder={t('pages-misc-2:skillCreate.descriptionPlaceholder')}
                                    rows={2}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('pages-misc-2:skillCreate.modelOptional')}
                                </label>
                                <select
                                    value={model}
                                    onChange={onModelChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                    <option value="sonnet">Sonnet</option>
                                    <option value="opus">Opus</option>
                                    <option value="">{t('pages-misc-2:skillCreate.defaultOption')}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="bg-primary-50 rounded-xl p-6 border border-primary-100">
                        <h3 className="text-sm font-semibold text-primary-900 mb-2 flex items-center">
                            <FileText className="w-4 h-4 mr-2" />
                            {t('pages-misc-2:skillCreate.skillTips')}
                        </h3>
                        <ul className="text-sm text-primary-700 space-y-1">
                            <li>• {t('pages-misc-2:skillCreate.tipSavedUnderSubfolder')}</li>
                            <li>• {t('pages-misc-2:skillCreate.tipEnhancePlans')}</li>
                            <li>• {t('pages-misc-2:skillCreate.tipIncludeSteps')}</li>
                            <li>• {t('pages-misc-2:skillCreate.tipUseDiscover')}</li>
                        </ul>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="flex items-center border-b border-gray-200">
                            <button
                                type="button"
                                onClick={() => setActiveTab('edit')}
                                className={`px-4 py-3 text-sm font-medium transition-colors ${
                                    activeTab === 'edit'
                                        ? 'bg-white text-primary-600 border-b-2 border-primary-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="w-4 h-4" />
                                    <span>{t('pages-misc-2:skillCreate.editMarkdown')}</span>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('preview')}
                                className={`px-4 py-3 text-sm font-medium transition-colors ${
                                    activeTab === 'preview'
                                        ? 'bg-white text-primary-600 border-b-2 border-primary-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Eye className="w-4 h-4" />
                                    <span>{t('pages-misc-2:skillCreate.preview')}</span>
                                </div>
                            </button>
                        </div>

                        <div className="p-4">
                            {activeTab === 'edit' ? (
                                <textarea
                                    ref={textareaRef}
                                    value={skillMarkdown}
                                    onChange={onMarkdownChange}
                                    placeholder={t('pages-misc-2:skillCreate.enterMarkdownPlaceholder')}
                                    className="w-full min-h-[500px] font-mono text-sm border-0 focus:ring-0 resize-none outline-none"
                                    style={{ height: 'auto' }}
                                />
                            ) : (
                                <div className="prose prose-sm max-w-none min-h-[500px] overflow-auto">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{previewBody}</ReactMarkdown>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
